import { FC, memo, useMemo } from 'react';
import { useDigitalFormsSourceTenantsContext } from '@frontend/digital-forms-scope';
import { useTranslation } from '@frontend/i18n';
import { BannerNotification } from '@frontend/design-system';
import { useFormsSubmissionsV2Context } from '../../../../../../../../../../../../../../provider';
import { useWritebackStore } from '../../../../../../../../../provider';
import { bannerNotificationStyle } from './duplicate-record-info-content.style';

interface SourceInfo {
  text: string;
  isValid: boolean;
}

interface DuplicateRecordProps {
  sourceTenantId: string;
}

const DuplicateRecord: FC<DuplicateRecordProps> = ({ sourceTenantId }) => {
  const { locationId = '' } = useFormsSubmissionsV2Context();
  const { getSourceTenantDetails } = useDigitalFormsSourceTenantsContext();

  const { isValid, text } = useMemo<SourceInfo>(() => {
    if (!sourceTenantId) {
      return {
        text: '',
        isValid: false,
      };
    }

    const sourceTenant = getSourceTenantDetails(locationId, sourceTenantId, true);
    if (!sourceTenant) {
      return {
        text: '',
        isValid: false,
      };
    }

    return {
      text: sourceTenant.parentSourceName
        ? `${sourceTenant.parentSourceName} | ${sourceTenant.sourceName}`
        : sourceTenant.sourceName,
      isValid: true,
    };
  }, [getSourceTenantDetails, locationId, sourceTenantId]);

  if (!isValid) {
    return null;
  }

  return <li key={sourceTenantId}>{text}</li>;
};

const DuplicateRecordsInfoContent: FC = () => {
  const { t } = useTranslation('forms');
  const { sourceTenantIdsWithDuplicateRecords } = useWritebackStore(['sourceTenantIdsWithDuplicateRecords']);
  const duplicateRecordsCount = sourceTenantIdsWithDuplicateRecords.length;

  return (
    <BannerNotification status='warn' css={bannerNotificationStyle}>
      {t('We have detected duplicate records in {{count}} of your systems:', { count: duplicateRecordsCount })}
      <ul>
        {sourceTenantIdsWithDuplicateRecords.map((sourceTenantId) => (
          <DuplicateRecord key={sourceTenantId} sourceTenantId={sourceTenantId} />
        ))}
      </ul>
    </BannerNotification>
  );
};

export default memo(DuplicateRecordsInfoContent);
